/**
 * ChatActionsSheetContent
 *
 * Content rendered inside the ChatActionsSheet (via SheetService). UI
 * orchestration only: consumes the three locally-cached predicates and the
 * block-conversation / block-user / mute mutations from `useChatActions`, builds
 * the three action rows in Flutter order with predicate-driven labels, and routes
 * each row tap to its confirm-flow handler from `buildChatActionConfirmFlows`
 * (block conversation / block user / mute — all three are now wired to their real
 * confirm → mutate → toast flows).
 *
 * Flutter source of truth:
 * `lib/domain/entity/bottom_sheet/chat_actions_bottom_sheet.dart`:
 *  - `onBuildCompleted` (67-75): resolves muted / conversation-blocked /
 *    user-blocked, then builds the list.
 *  - `getChatActions` (152-179): the item set, order (block conversation →
 *    block user → mute), icons, and conditional labels.
 *
 * Predicate-key note (see story `## Context`): the web uses the *separated*
 * secure-storage keys — conversation-blocked ← `BLOCKED_CONVERSATIONS`,
 * user-blocked ← `BLOCKED_USERS` — rather than mirroring Flutter's box
 * collision (both reads hit the same `BlockedUser` box in this older sheet).
 */
import React, { useMemo } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ChatActionType } from '@/domain/enums/chat/chatActionType';
import { useChatActions } from './hooks/useChatActions';
import { ChatActionItem } from './ChatActionItem';
import { buildChatActionConfirmFlows } from './chatActionsConfirmFlows';

export interface ChatActionsSheetContentProps {
  peerId: string;
  peerFirstName: string;
  /**
   * Invoked with the new blocked state on a successful block/unblock so the
   * hosting chat page flips `useChat.isConversationBlocked` live. Not called on
   * the failure path (the optimistic write was rolled back). See
   * `ChatActionsSheetConfig.onConversationBlockChanged`.
   */
  onConversationBlockChanged?: (block: boolean) => void;
  screenKey?: string;
}

interface ChatActionRow {
  type: ChatActionType;
  iconPath: string;
  label: string;
}

export const ChatActionsSheetContent: React.FC<ChatActionsSheetContentProps> = ({
  peerId,
  peerFirstName,
  onConversationBlockChanged,
  screenKey,
}) => {
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // State + business-logic calls live in the feature hook; the component keeps
  // only UI orchestration (Clean Architecture flow).
  const {
    muted,
    conversationBlocked,
    userBlocked,
    setBlockConversation,
    blockUser,
    setMute,
  } = useChatActions({ peerId });

  // Build the row view-models in Flutter `getChatActions` order:
  // block conversation → block user → mute. Label flips on each predicate.
  const rows = useMemo<ChatActionRow[]>(
    () => [
      {
        type: ChatActionType.blockConversation,
        iconPath: '/assets/images/block.png',
        label: conversationBlocked
          ? l10n.chat.actions.unblockConversation
          : l10n.chat.actions.blockConversation,
      },
      {
        type: ChatActionType.blockUser,
        iconPath: '/assets/images/block_user.png',
        label: (userBlocked
          ? l10n.chat.actions.unblockUser
          : l10n.chat.actions.blockUser
        ).replace('{peerFirstName}', peerFirstName),
      },
      {
        type: ChatActionType.mute,
        iconPath: '/assets/images/notification.png',
        label: muted
          ? l10n.chat.actions.unmuteNotifications
          : l10n.chat.actions.muteNotifications,
      },
    ],
    [conversationBlocked, userBlocked, muted, peerFirstName, l10n],
  );

  // All three confirm → mutate → side-effect handlers (block conversation /
  // block user / mute) live in the colocated `buildChatActionConfirmFlows`
  // factory, keeping this component UI-orchestration-only. Each handler preserves
  // the Flutter pop-order and side-effect asymmetry — see that module for the
  // parity reasoning. Rebuilt whenever the predicates or any binding change so the
  // confirm copy always reflects the *current* state.
  const { handleBlockConversation, handleBlockUser, handleMuteConversation } =
    useMemo(
      () =>
        buildChatActionConfirmFlows({
          l10n,
          conversationBlocked,
          userBlocked,
          muted,
          peerFirstName,
          setBlockConversation,
          blockUser,
          setMute,
          onConversationBlockChanged,
          screenKey,
        }),
      [
        l10n,
        conversationBlocked,
        userBlocked,
        muted,
        peerFirstName,
        setBlockConversation,
        blockUser,
        setMute,
        onConversationBlockChanged,
        screenKey,
      ],
    );

  // All three actions are now wired to their real confirm → mutate → toast flows
  // (Flutter `onActionTap`, 187-205).
  const handleActionTap = (type: ChatActionType): void => {
    switch (type) {
      case ChatActionType.blockConversation:
        handleBlockConversation();
        break;
      case ChatActionType.blockUser:
        handleBlockUser();
        break;
      case ChatActionType.mute:
        handleMuteConversation();
        break;
    }
  };

  return (
    <div
      {...qaAttr({ id: 'chat-actions-content' })}
      className="flex flex-col"
      style={{
        padding: responsive.spacing.xl,
        gap: responsive.spacing.xl,
      }}
    >
      {rows.map((row) => (
        <ChatActionItem
          key={row.type}
          type={row.type}
          iconPath={row.iconPath}
          label={row.label}
          onClick={() => handleActionTap(row.type)}
        />
      ))}
    </div>
  );
};
